import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import API, { UPLOADS_URL } from '../api/axios';
import SEO from '../components/SEO';
import PageBanner from '../components/PageBanner';

const sectionTitle = 'text-primary font-bold text-[14px] uppercase tracking-wide mt-5 mb-2 flex items-center gap-2';
const sectionTitleIcon = 'w-1 h-4 rounded-full bg-gradient-to-b from-secondary to-primary inline-block';
const thCls = 'text-left px-3 py-2 text-[12px] font-semibold text-white bg-primary whitespace-nowrap';
const amountCls = 'px-3 py-2 text-left whitespace-nowrap text-body';
const tdCls = 'px-3 py-2 text-[13px] text-gray-700 whitespace-nowrap';
const totalCls = 'px-3 py-2 text-left whitespace-nowrap font-bold text-primary';

const resolveImage = (src) => {
  if (!src) return '';
  if (src.startsWith('http') || src.startsWith('/')) return src;
  return `${UPLOADS_URL}/${src}`;
};

const getExtra = (s) => {
  if (!s || !s.extra) return {};
  if (typeof s.extra === 'object') return s.extra;
  try {
    return JSON.parse(s.extra);
  } catch {
    return {};
  }
};

const FeeCard = ({ title, children }) => (
  <div className="card p-0 overflow-hidden h-full">
    <div className="bg-gradient-to-r from-primary to-primary-dark px-5 py-3.5">
      <h3 className="text-white font-bold text-lg">{title}</h3>
    </div>
    <div className="p-4 md:p-5">{children}</div>
  </div>
);

const SectionTitle = ({ children }) => (
  <div className={sectionTitle}><span className={sectionTitleIcon}></span>{children}</div>
);

const MiniTable = ({ rows, head }) => (
  <div className="overflow-x-auto rounded-lg border border-gray-100">
    <table className="w-full text-sm">
      {head && (
        <thead>
          <tr className="bg-primary">{head.map((h, i) => <th key={i} className={thCls} >{h}</th>)}</tr>
        </thead>
      )}
      <tbody>
        {rows.map((row, idx) => (
          <tr key={row.label || row.class || idx} className={idx % 2 === 1 ? 'bg-[#fbfdff]' : 'bg-white'}>
            {row.cells}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Fees = () => {
  const [bannerImage, setBannerImage] = useState(null);
  const [bannerReady, setBannerReady] = useState(false);
  const [sections, setSections] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    API.get('/fees/sections').then(res => {
      if (res.data?.data) setSections(res.data.data);
    }).catch(() => {}).finally(() => {
      setBannerReady(true);
      setLoaded(true);
    });
  }, []);

  const byKey = useMemo(() => {
    const map = {};
    sections.forEach(s => { map[s.section_key] = s; });
    return map;
  }, [sections]);

  const hero = byKey.hero;
  const intro = byKey.intro;
  const quarterly = byKey.quarterly_fees;
  const concession = byKey.concession;
  const transportSec = byKey.transport;
  const cta = byKey.cta;

  useEffect(() => {
    if (hero?.image) setBannerImage(hero.image);
  }, [hero]);

  const qExtra = getExtra(quarterly);
  const admissionRows = (qExtra.admission || []).map(r => ({
    label: r.label,
    cells: (<><td className={tdCls}>{r.label}</td><td className={amountCls + ' font-semibold text-primary'}>₹{r.amount}</td></>),
  }));
  const quarterlyRows = (qExtra.rows || []).map(r => ({
    label: r.class,
    cells: (
      <>
        <td className={tdCls + ' font-medium text-primary'}>{r.class}</td>
        <td className={amountCls}>₹{r.q1}</td>
        <td className={amountCls}>₹{r.q2}</td>
        <td className={amountCls}>₹{r.q3}</td>
        <td className={amountCls}>₹{r.q4}</td>
        <td className={totalCls}>₹{r.total}</td>
      </>
    ),
  }));
  const roboticRow = qExtra.robotic ? [{
    label: 'Robotic',
    cells: (<><td className={tdCls}>{qExtra.robotic.label || 'Class I to VIII'}</td><td className={amountCls}>₹{qExtra.robotic.quarter} / quarter</td><td className={totalCls}>₹{qExtra.robotic.year} / year</td></>),
  }] : [];
  const optionalRows = (qExtra.optional || []).map(r => ({
    label: r.label,
    cells: (<><td className={tdCls}>{r.label}</td><td className={amountCls + ' font-semibold text-primary'}>₹{r.amount}</td></>),
  }));
  const paymentRows = (qExtra.payment || []).map(r => ({
    label: r.label,
    cells: (
      <div className="flex justify-between rounded-lg bg-[#f7f8fa] px-3 py-2">
        <span className="font-semibold text-primary">{r.label}</span>
        <span className="text-body">{r.value}</span>
      </div>
    ),
  }));

  const concessionRows = (getExtra(concession).rows || []).map(r => ({
    label: r.label,
    cells: (<><td className="px-4 py-3 font-medium text-primary">{r.label}</td><td className="px-4 py-3 text-right text-body">{r.value}</td></>),
  }));

  const transportExtra = getExtra(byKey.transport_fees || byKey.transport);
  const defaultTransportFeeData = {
    title: "Transport Fee (Payable quarterly: 15th April, 15th July, 15th October, 15th January)*",
    fee_structure: [
      {
        distance_slab: "Up to 4 KM",
        quarters: { q1: 5940, q2: 8910, q3: 8910, q4: 8910 }
      },
      {
        distance_slab: "4 - 6 KM",
        quarters: { q1: 6480, q2: 9720, q3: 9720, q4: 9720 }
      },
      {
        distance_slab: "6 – 8 KM",
        quarters: { q1: 7020, q2: 10530, q3: 10530, q4: 10530 }
      },
      {
        distance_slab: "Above 8 KM",
        quarters: { q1: 7560, q2: 11340, q3: 11340, q4: 11340 }
      }
    ]
  };

  const transportFeeData = useMemo(() => {
    if (transportExtra?.fee_structure && Array.isArray(transportExtra.fee_structure)) {
      return {
        title: transportExtra.title || defaultTransportFeeData.title,
        fee_structure: transportExtra.fee_structure.map(row => {
          const q = row.quarters || {};
          return {
            distance_slab: row.distance_slab || row.distance,
            quarters: {
              q1: q.q1 ?? q['1st_qtr_apr_may'] ?? '',
              q2: q.q2 ?? q['2nd_qtr_july_sep'] ?? '',
              q3: q.q3 ?? q['3rd_qtr_oct_dec'] ?? '',
              q4: q.q4 ?? q['4th_qtr_jan_march'] ?? '',
            }
          };
        })
      };
    }
    return defaultTransportFeeData;
  }, [transportExtra]);

  const hasQuarterly = quarterlyRows.length > 0 || admissionRows.length > 0;

  return (
    <>
      <SEO
        title="Fee Structure (2027-28)"
        description="Fee Structure for St. Andrews World School, Indirapuram."
        keywords="fee structure, ICSE school fees Indirapuram, school fees Indirapuram"
      />

      <PageBanner
        title="Schools Fees List"
        breadcrumbs={[
          { label: 'Home', link: '/' },
          { label: 'Admission', link: '/admission' },
          { label: 'Schools Fees List' },
        ]}
        backgroundImage={bannerImage ? resolveImage(bannerImage) : null}
        fallback={`${process.env.PUBLIC_URL}/images/page-banners/admission-guidliness.jpg`}
        loading={!bannerReady}
      />

      <section className="section bg-white">
        <div className="container max-w-[1200px]">

          {loaded && (
            <div className="text-center mb-8">
              <div className="kicker">{intro?.kicker || 'Fee Structure — 2027–28 Session'}</div>
              <h2 className="h-section text-2xl md:text-3xl font-bold mt-2">{intro?.title || 'St. Andrews World School'}</h2>
              {intro?.description && (
                <div className="text-body text-sm mt-3 max-w-2xl mx-auto" dangerouslySetInnerHTML={{ __html: intro.description }} />
              )}
              {(intro?.button_text || intro?.button2_text) && (
                <div className="mt-5 flex flex-col justify-center gap-3 sm:flex-row sm:flex-wrap">
               
                
                </div>
              )}
            </div>
          )}

          {loaded && hasQuarterly && (
            <div className="grid grid-cols-1 gap-6 items-start">
              <FeeCard title={quarterly?.title || 'Quarterly'}>
                {admissionRows.length > 0 && (
                  <>
                    <SectionTitle>Admission / Registration</SectionTitle>
                    <MiniTable rows={admissionRows} />
                  </>
                )}
                {qExtra.note && (
                  <div className="px-3 py-2.5 text-[11px] text-muted italic">{qExtra.note}</div>
                )}

                {quarterlyRows.length > 0 && (
                  <>
                    <SectionTitle>Mandatory Fees — Composite Fees</SectionTitle>
                    <MiniTable
                      head={['Class', 'Quarter 1', 'Quarter 2', 'Quarter 3', 'Quarter 4', 'Total']}
                      rows={quarterlyRows}
                    />
                  </>
                )}

                {roboticRow.length > 0 && (
                  <>
                    <SectionTitle>Robotic Fees</SectionTitle>
                    <MiniTable rows={roboticRow} />
                  </>
                )}

                {optionalRows.length > 0 && (
                  <>
                    <SectionTitle>Optional Fees</SectionTitle>
                    <MiniTable rows={optionalRows} />
                  </>
                )}

                {paymentRows.length > 0 && (
                  <>
                    <SectionTitle>Payment Details</SectionTitle>
                    <div className="space-y-2 text-[13px]">
                      {paymentRows.map((r, i) => <div key={i}>{r.cells}</div>)}
                    </div>
                  </>
                )}
              </FeeCard>
            </div>
          )}

          {loaded && concessionRows.length > 0 && (
            <div className="card mt-8 p-6 md:p-7">
              <h3 className="h-section text-xl md:text-2xl font-bold mb-4">{concession?.title || 'Concession'}</h3>
              <div className="overflow-x-auto rounded-lg border border-gray-100">
                <table className="w-full text-sm">
                  <tbody>
                    {concessionRows.map((r, idx) => (
                      <tr key={r.label + idx} className={idx % 2 === 1 ? 'bg-[#fbfdff]' : 'bg-white'}>
                        {r.cells}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {loaded && (
            <div className="card mt-8 p-0 overflow-hidden">
              <div className="bg-gradient-to-r from-primary to-primary-dark px-5 py-3.5">
                <h3 className="text-white font-bold text-lg">{transportFeeData.title}</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-[620px] w-full text-sm">
                  <thead>
                    <tr className="bg-light border-b border-gray-100">
                      <th className="text-left px-4 py-3 font-semibold text-primary">Distance Slab</th>
                      <th className="text-left px-4 py-3 font-semibold text-primary whitespace-nowrap">1st Qtr<br/><span className="text-[11px] font-normal text-muted">(Apr-May)</span></th>
                      <th className="text-left px-4 py-3 font-semibold text-primary whitespace-nowrap">2nd Qtr<br/><span className="text-[11px] font-normal text-muted">(July-Sep)</span></th>
                      <th className="text-left px-4 py-3 font-semibold text-primary whitespace-nowrap">3rd Qtr<br/><span className="text-[11px] font-normal text-muted">(Oct-Dec)</span></th>
                      <th className="text-left px-4 py-3 font-semibold text-primary whitespace-nowrap">4th Qtr<br/><span className="text-[11px] font-normal text-muted">(Jan-March)</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {transportFeeData.fee_structure.map((row, idx) => (
                      <tr key={idx} className={['border-b border-gray-100 last:border-b-0', idx % 2 === 1 ? 'bg-white' : 'bg-[#fbfdff]'].join(' ')}>
                        <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-800">{row.distance_slab}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-body">₹{row.quarters.q1}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-body">₹{row.quarters.q2}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-body">₹{row.quarters.q3}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-body">₹{row.quarters.q4}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        
          {loaded && (
            <div className="mt-5 text-center text-xs text-muted">
              St. Andrews World School • Fee Structure for the 2027–28 Session
            </div>
          )}

        </div>
      </section>
    </>
  );
};

export default Fees;